import React, { useEffect } from "react";
import { useGetLoggedInUserDetails } from "../../../aAdminTabTeam2/hooksAndFunctions/customHooks/useGetLoggedInUserDetails";
import { useGetCountryListForDropdowns } from "../../../api/services/client/clientApis";
import { ProfileResponse } from "../../../api/services/client/models";
import { useGetUsersDetailByRoleMutation } from "../../../api/services/user/userApi";
import {
  AccordionHeader,
  AccordionHeaderText,
  MuiBox,
  MuiDoubleLabel,
  MuiGrid,
} from "../../../components";
import {
  PAGE_IDS,
  PATTERN,
  ROLE_IDS,
  SITE_CORE_COUNTRIES_VALUE
} from "../../../helpers/constants";
import { getDateFormatByCountry } from "../../../helpers/constants/countryDateFormat";
import {
  checkWithLowerCase,
  convertToUserFormat,
  getCountryTitleByKey,
  getFullNameById,
  getValueOrBlank,
  isEmpty,
  isNotEmpty,
  replaceCharPattern,
} from "../../../helpers/utils";
import { useGridContainer } from "../../../hooks/useGridContainer";
import { useMediaQueries } from "../../../hooks/useMediaQueries";
import { useSiteCore } from "../../../hooks/useSiteCore";
import { createClientSiteCoreKeys } from "../../createClient/siteCore/SiteCoreKeys";
import { ViewProfileWrapper } from "./ViewProfile.style";

const pageId = PAGE_IDS.VIEW_PROFILE;

interface ViewProfileProps {
  data: ProfileResponse;
  isExpanded?: boolean;
  onAccordionChange: (event: React.SyntheticEvent, expanded: boolean) => void;
}

const ViewProfile: React.FC<ViewProfileProps> = ({
  data,
  isExpanded,
  onAccordionChange,
}) => {
  const { gridSizeMedium: gridSize, gridSizeMediumClass } = useGridContainer();

  const { isMobile } = useMediaQueries();
  const { loggedInUser } = useGetLoggedInUserDetails();
  const userCountryDateFormat = getDateFormatByCountry(
    loggedInUser?.countryCode ?? "",
  );

  const { fetchLabel, fetchValueByKeyOfDropdown, fetchComponentTitle } =
    useSiteCore();
  const {
    createClientProfileTitle,
    siretNumber: siretNumberKey,
    btwNummer: btwNummerKey,
    nifNumber: nifNumberKey,
    abnNumber: abnNumberKey,
    nzbnNumber: nzbnNumberKey,
    binNumber: binNumberKey,
    cinNumber: cinNumberKey,
    tinNumber: tinNumberKey,
    qualifiedInvoiceNumber: qualifiedInvoiceNumberKey,
    clientType,
    companyName,
    englishCompanyName,
    industry,
    localClientRelationshipOwner,
    addressCountry,
    billingCountry,
    taxIDVATNumber,
    dedicatedProjectManager,
    originDate,
    salesOffice: salesOfficeKey,
  } = createClientSiteCoreKeys;

  const getLabel = (key: string) => {
    return fetchLabel(createClientProfileTitle, key);
  };

  const getDropdownValues = (
    key: keyof typeof createClientSiteCoreKeys,
    value: string,
  ) => {
    if (isEmpty(value)) return "";
    return fetchValueByKeyOfDropdown(
      createClientProfileTitle,
      createClientSiteCoreKeys[key],
      value,
    );
  };

  const {
    mutateAsync: getDedicatedProjectManagersByRole,
    data: dedicatedProjectManagerData,
  } = useGetUsersDetailByRoleMutation();

  const {
    mutateAsync: getLocalClientRelationshipOwnersByRole,
    data: localClientRelationshipOwnerData,
  } = useGetUsersDetailByRoleMutation();

  useEffect(() => {
    getDedicatedProjectManagersByRole({ roleIds: [ROLE_IDS.projectManager] });
    getLocalClientRelationshipOwnersByRole({ roleIds: [ROLE_IDS.sales] });
  }, []);

  const { data: countryListData } = useGetCountryListForDropdowns();

  const {
    FRANCE,
    SPAIN,
    NETHERLANDS,
    JAPAN,
    AUSTRALIA,
    INDIA,
    NEW_ZEALAND,
    BANGLADESH,
    SRI_LANKA,
  } = SITE_CORE_COUNTRIES_VALUE;

  const renderCompanyIdLabel = () => {
    const addressCountry = data.addressCountryId;
    if (!addressCountry || !data.businessIdentificationNumber) return null;

    const countryKeyMap: Partial<
      Record<
        keyof typeof SITE_CORE_COUNTRIES_VALUE,
        keyof typeof createClientSiteCoreKeys
      >
    > = {
      [FRANCE]: siretNumberKey,
      [NETHERLANDS]: btwNummerKey,
      [SPAIN]: nifNumberKey,
      [AUSTRALIA]: abnNumberKey,
      [NEW_ZEALAND]: nzbnNumberKey,
      [JAPAN]: qualifiedInvoiceNumberKey,
      [INDIA]: cinNumberKey,
      [BANGLADESH]: binNumberKey,
      [SRI_LANKA]: tinNumberKey,
    };

    // Find matching country key directly
    const matchedKey = Object.entries(countryKeyMap).find(([country]) =>
      checkWithLowerCase(addressCountry, country),
    )?.[1];

    if (!matchedKey) return null;

    let businessIdentificationNumber = data.businessIdentificationNumber;
    if (
      businessIdentificationNumber &&
      checkWithLowerCase(addressCountry, FRANCE)
    ) {
      const { formattedText, remainingText } = replaceCharPattern(
        PATTERN.SIRET_NUMBER,
        businessIdentificationNumber,
      );
      businessIdentificationNumber = formattedText + remainingText.join("");
    }

    return (
      <MuiGrid size={gridSize} className={gridSizeMediumClass}>
        <MuiDoubleLabel
          id={`${pageId}-${matchedKey}`}
          label={getLabel(matchedKey)}
          replaceEmptyWithDash
          value={businessIdentificationNumber}
        />
      </MuiGrid>
    );
  };

  return (
    <ViewProfileWrapper>
      <AccordionHeader
        title={
          <AccordionHeaderText
            label={fetchComponentTitle(createClientProfileTitle)}
          />
        }
        isExpanded={isExpanded}
        onChange={(event, expanded) => onAccordionChange(event, expanded)}
      >
        {data && (
          <MuiBox className="view-profile-container">
            <MuiGrid
              container
              spacing={isMobile ? 0 : 2}
              className="client-details"
            >
              <MuiGrid size={gridSize} className={gridSizeMediumClass}>
                <MuiDoubleLabel
                  id={`${pageId}-clientType`}
                  label={getLabel(clientType)}
                  replaceEmptyWithDash
                  value={getDropdownValues(
                    "clientTypeList",
                    data.clientTypeId ?? "",
                  )}
                />
              </MuiGrid>
              <MuiGrid size={gridSize} className={gridSizeMediumClass}>
                <MuiDoubleLabel
                  id={`${pageId}-clientName`}
                  label={getLabel(companyName)}
                  isValueWithEllipsis
                  replaceEmptyWithDash
                  value={data.clientName}
                />
              </MuiGrid>
              <MuiGrid size={gridSize} className={gridSizeMediumClass}>
                <MuiDoubleLabel
                  id={`${pageId}-englishClientName`}
                  label={getLabel(englishCompanyName)}
                  isValueWithEllipsis
                  replaceEmptyWithDash
                  value={data.englishName}
                />
              </MuiGrid>
              <MuiGrid size={gridSize} className={gridSizeMediumClass}>
                <MuiDoubleLabel
                  id={`${pageId}-industry`}
                  label={getLabel(industry)}
                  replaceEmptyWithDash
                  value={data.industryName}
                />
              </MuiGrid>
              <MuiGrid size={gridSize} className={gridSizeMediumClass}>
                <MuiDoubleLabel
                  id={`${pageId}-localClientRelationshipOwner`}
                  label={getLabel(localClientRelationshipOwner)}
                  value={getFullNameById(
                    data.localClientRelationshipOwnerId ?? "",
                    localClientRelationshipOwnerData,
                  )}
                />
              </MuiGrid>
            </MuiGrid>
            <MuiBox className="separator-space" />

            <MuiGrid
              container
              spacing={isMobile ? 0 : 2}
              className="client-details"
            >
              <MuiGrid size={gridSize} className={gridSizeMediumClass}>
                <MuiDoubleLabel
                  id={`${pageId}-addressCountry`}
                  label={getLabel(addressCountry)}
                  value={getCountryTitleByKey(
                    data.addressCountryId,
                    countryListData,
                  )}
                />
              </MuiGrid>
              <MuiGrid size={gridSize} className={gridSizeMediumClass}>
                <MuiDoubleLabel
                  id={`${pageId}-billingCountry`}
                  label={getLabel(billingCountry)}
                  value={getCountryTitleByKey(
                    data.billingCountryId,
                    countryListData,
                  )}
                />
              </MuiGrid>
              <MuiGrid size={gridSize} className={gridSizeMediumClass}>
                <MuiDoubleLabel
                  id={`${pageId}-salesOffice`}
                  label={getLabel(salesOfficeKey)}
                  replaceEmptyWithDash
                  value={data.salesOffice.name}
                />
              </MuiGrid>
              <MuiGrid size={gridSize} className={gridSizeMediumClass}>
                <MuiDoubleLabel
                  id={`${pageId}-taxIdVatNumber`}
                  label={getLabel(taxIDVATNumber)}
                  replaceEmptyWithDash
                  value={data.vatNumber}
                />
              </MuiGrid>
              {renderCompanyIdLabel()}
            </MuiGrid>

            <MuiBox className="separator-space" />

            <MuiGrid
              container
              spacing={isMobile ? 0 : 2}
              className="client-details"
            >
              <MuiGrid size={gridSize} className={gridSizeMediumClass}>
                <MuiDoubleLabel
                  id={`${pageId}-projectManager`}
                  label={getLabel(dedicatedProjectManager)}
                  value={getFullNameById(
                    data.dedicatedProjectManagerId ?? "",
                    dedicatedProjectManagerData,
                  )}
                />
              </MuiGrid>

              <MuiGrid size={gridSize} className={gridSizeMediumClass}>
                <MuiDoubleLabel
                  id={`${pageId}-originDate`}
                  label={getLabel(originDate)}
                  replaceEmptyWithDash
                  value={
                    isNotEmpty(data.originDate)
                      ? convertToUserFormat(
                          getValueOrBlank(data?.originDate),
                          userCountryDateFormat,
                        )
                      : ""
                  }
                />
              </MuiGrid>
            </MuiGrid>
          </MuiBox>
        )}
      </AccordionHeader>
    </ViewProfileWrapper>
  );
};

export default ViewProfile;
